import { useTranslation } from 'react-i18next'
import {
  invalidateCustomerDocumentCategoryCache,
  useDeleteCustomerDocumentCategory,
  useGetCustomerDocumentCategories,
  useGetSystemAdminDocumentCategoriesForCustomer,
} from 'src/api/customer-document-category'
import { useCustomerCtx, useDialogCtx } from 'src/hooks/context-hooks'
import { DocumentCategoryTable } from 'src/components/document-categories/DocumentCategoryTable'
import { UpdateCustomerDocumentCategoryDialog } from 'src/components/document-categories/customer-admin/UpdateCustomerDocumentCategoryDialog'
import StaticTabsComponent, { TabProps } from 'src/components/document-categories/customer-admin/StaticTabsComponent'
import { UserType } from 'src/components/document-categories/constants'
import { ApproveDocumentCategoryDialog } from 'src/components/document-categories/customer-admin/ApproveDocumentCategoryDialog'
import { useCallback, useMemo } from 'react'
import { toast } from 'react-toastify'
import { MoveAndDeleteDocumentCategoryDialog } from 'src/components/document-categories/customer-admin/MoveAndDeleteDocumentCategoryDialog'

export const CustomerDocumentCategoryTableContainer = () => {
  const { t: translate } = useTranslation(['document-category', 'common', 'common-api-message'])
  const { customer } = useCustomerCtx()
  const { showDialog, showConfirmDialog } = useDialogCtx()
  const { data: customerDocumentCategories } = useGetCustomerDocumentCategories(customer?.id)
  const { data: systemAdminDocumentCategoriesForCustomer } = useGetSystemAdminDocumentCategoriesForCustomer(
    customer?.id,
  )
  const { mutateAsync: deleteMutation } = useDeleteCustomerDocumentCategory(customer?.id)

  const onUpdate = useCallback(
    async id => {
      const added = await showDialog(
        UpdateCustomerDocumentCategoryDialog,
        {
          componentProps: { id: id as EntityId, customerId: customer?.id },
          opts: {
            maxWidth: 'sm',
            fullWidth: true,
          },
        },
        undefined,
        true,
      )
      if (!added) return
    },
    [showDialog, customer?.id],
  )

  const moveAndDelete = useCallback(
    async id => {
      const updatedAndDeleted = await showDialog(
        MoveAndDeleteDocumentCategoryDialog,
        {
          componentProps: {
            id: id as EntityId,
            customerId: customer?.id,
            customerDocumentCategories: customerDocumentCategories?.items,
          },
          opts: {
            maxWidth: 'sm',
            fullWidth: true,
          },
        },
        undefined,
        true,
      )
      if (updatedAndDeleted) return
    },
    [showDialog, customer?.id, customerDocumentCategories?.items],
  )

  const onDeleteDocumentCategory = useCallback(
    (id: EntityId) => {
      showConfirmDialog(
        translate('deleteDocumentCategory', { ns: 'document-category' }),
        translate('areYouSureDeleteDocumentCategory', { ns: 'document-category' }),
        { acceptText: translate('yes', { ns: 'common' }), cancelText: translate('no', { ns: 'common' }) },
        async pop => {
          await deleteMutation(id, {
            onSuccess: () => {
              toast.success(translate('successfullyRemoved', { ns: 'common-api-message' }))
              invalidateCustomerDocumentCategoryCache.getCustomerDocumentCategories(customer?.id)
              invalidateCustomerDocumentCategoryCache.getCustomerAndAdminDocumentCategories(customer?.id)
              pop()
            },
            onError: (error: any) => {
              if (error && error['data'] == 'CannotDeleteDocumentCategory') {
                toast.error(translate('CannotDeleteDocumentCategory', { ns: 'document-category' }))
              }
              if (error && error['data'] == 'CannotDeleteSystemGeneratedDocumentCategory') {
                toast.error(translate('cannotDeleteSystemGeneratedDocumentCategory', { ns: 'document-category' }))
              }
            },
          })
        },
      )
    },
    [showConfirmDialog, translate, deleteMutation, customer?.id],
  )
  const onApproveDocumentCategory = useCallback(
    async (id: EntityId) => {
      await showDialog(
        ApproveDocumentCategoryDialog,
        {
          componentProps: { customerId: customer?.id, id: id as EntityId, isEnabled: true },
          opts: {
            maxWidth: 'sm',
            fullWidth: true,
          },
        },

        undefined,
        true,
      )
    },
    [showDialog, customer?.id],
  )
  const onRejectDocumentCategory = useCallback(
    async (id: EntityId) => {
      await showDialog(
        ApproveDocumentCategoryDialog,
        {
          componentProps: { customerId: customer?.id, id: id as EntityId, isEnabled: false },
          opts: {
            maxWidth: 'sm',
            fullWidth: true,
          },
        },

        undefined,
        true,
      )
    },
    [showDialog, customer?.id],
  )

  const combinedDataLength = useMemo(() => {
    const customerItems = customerDocumentCategories?.items ?? []
    const enabledSystemItems =
      systemAdminDocumentCategoriesForCustomer?.filter(
        category => category.isEnabled === true || category.isForcePushed === true,
      ) ?? []

    return customerItems.length + enabledSystemItems.length
  }, [customerDocumentCategories?.items, systemAdminDocumentCategoriesForCustomer])

  const CustomerDocumentCategoryTabs: TabProps[] = useMemo(
    () => [
      {
        index: 0,
        label: translate('myDocumentCategories', { ns: 'document-category' }),
        count: combinedDataLength ?? 0,
        content: (
          <DocumentCategoryTable
            userType={UserType.CustomerAdmin}
            customerId={customer?.id}
            data={[
              ...(customerDocumentCategories?.items ?? []),
              ...(systemAdminDocumentCategoriesForCustomer?.filter(
                category => category.isEnabled === true || category.isForcePushed === true,
              ) ?? []),
            ]}
            onEdit={onUpdate}
            onDelete={onDeleteDocumentCategory}
            onApprove={onApproveDocumentCategory}
            onReject={onRejectDocumentCategory}
            onMoveAndDelete={moveAndDelete}
            showActions={false}
            showKebabActions={true}
          />
        ),
      },
      {
        index: 1,
        label: translate('systemUserGeneratedDocumentCategories', { ns: 'document-category' }),
        count: systemAdminDocumentCategoriesForCustomer?.length ?? 0,
        content: (
          <DocumentCategoryTable
            userType={UserType.CustomerAdmin}
            customerId={customer?.id}
            data={systemAdminDocumentCategoriesForCustomer}
            onEdit={onUpdate}
            onDelete={onDeleteDocumentCategory}
            onApprove={onApproveDocumentCategory}
            onReject={onRejectDocumentCategory}
            onMoveAndDelete={moveAndDelete}
            showActions={true}
            showKebabActions={false}
          />
        ),
      },
    ],
    [
      customer?.id,
      customerDocumentCategories?.items,
      systemAdminDocumentCategoriesForCustomer,
      onUpdate,
      onDeleteDocumentCategory,
      onApproveDocumentCategory,
    ],
  )
  return <StaticTabsComponent tabs={CustomerDocumentCategoryTabs} />
}
